// ═══════════════════════════════════════════════════════════════════════════
// ASSENZE PER PERIODO (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Malattia, ferie o permesso registrati "dal … al …" (con il numero di protocollo del
// certificato per la malattia), dall'interessato o dall'ufficio del personale.
// Il periodo si trasforma in giornate nel cartellino, nella STESSA forma di dato delle voci
// speciali già esistenti (Ferie/Permesso/Malattia, dalle 08:00 per le ore standard, "speciale"), così
// cartellino, Report Ore, Excel e costi le trattano come sempre: le voci speciali non
// diventano mai costo di manodopera sulle commesse.
//
// Regole di sicurezza sui dati del cartellino:
//   • si scrivono solo i giorni feriali dentro il periodo lavorativo della persona;
//   • un giorno che ha già timbrature (lavoro o altra voce) NON viene toccato: si segnala;
//   • un cartellino già approvato non si modifica mai in automatico: si segnala il giorno
//     "da gestire a mano" (stessa regola delle ferie approvate);
//   • ogni giornata scritta porta l'id dell'assenza, così l'annullamento toglie SOLO quella.
// Il numero di protocollo resta nel documento dell'assenza (visibile solo all'interessato e
// all'ufficio): nel cartellino il giorno risulta semplicemente "Malattia".
// ═══════════════════════════════════════════════════════════════════════════
import { addDoc, collection, doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { parseData, dataStr, eFeriale } from "./periodoLavorativo";
import { segmentiGiornata, minutiDaOra } from "./segmentiGiornata";

export const TIPI_ASSENZA = [
  { id: "malattia", voce: "Malattia", label: "Malattia", protocollo: true },
  { id: "ferie",    voce: "Ferie",    label: "Ferie" },
  { id: "permesso", voce: "Permesso", label: "Permesso (giornata intera)" },
];
export const MAX_GIORNI_ASSENZA = 186; // oltre sei mesi: quasi certamente un errore di data

const fmtIt = (d) => `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

/** Controlli sul modulo, prima di scrivere qualsiasi cosa. Restituisce un messaggio o null. */
export function validaAssenza({ tipo, dal, al }) {
  if (!TIPI_ASSENZA.some(t => t.id === tipo)) return "Scegli il tipo di assenza";
  const d1 = parseData(dal), d2 = parseData(al);
  if (!d1 || !d2) return "Indica le date di inizio e di fine";
  if (d2 < d1) return "La data di fine è precedente a quella di inizio";
  if ((d2 - d1) / 86400000 + 1 > MAX_GIORNI_ASSENZA) return "Il periodo supera i sei mesi: controlla le date";
  return null;
}

/** Giornate del periodo, divise tra da scrivere e escluse a priori (pura, testabile). */
export function pianificaGiornate(dal, al, periodo) {
  const d1 = parseData(dal), d2 = parseData(al);
  const scrivibili = [], escluse = [];
  for (let d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
    const g = new Date(d);
    if (!eFeriale(g)) continue; // sabato, domenica e festivi: non si compilano nel cartellino
    if (periodo?.inizio && g < periodo.inizio) { escluse.push({ data: dataStr(g), motivo: "prima dell'assunzione" }); continue; }
    if (periodo?.fine && g > periodo.fine) { escluse.push({ data: dataStr(g), motivo: "dopo la cessazione" }); continue; }
    scrivibili.push(g);
  }
  return { scrivibili, escluse };
}

/** Orario di fine di una giornata intera che inizia alle 08:00 e dura `ore` ore. */
export function fineGiornata(ore) {
  const min = 8 * 60 + Math.round((Number(ore) > 0 ? Number(ore) : 8) * 60);
  return `${String(Math.floor(min / 60) % 24).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

/**
 * Scrive giornate "speciali" (Ferie, Permesso, Malattia) nel cartellino: solo i giorni feriali
 * del periodo lavorativo, senza toccare i giorni già compilati né i cartellini approvati.
 * La giornata dura le ore standard dell'azienda (08:00 → 08:00 + ore): il cartellino conta le
 * ore dalla differenza tra gli orari, quindi l'orario di fine deve corrispondere alle ore.
 * Usata da "Registra assenza" e dall'approvazione di ferie e permessi.
 * @param marcatore campi aggiunti a ogni timbratura per poterle ritrovare (es. { assenzaId })
 */
export async function scriviGiornateSpeciali({ orgId, uid, dal, al, voce, periodo, oreGiornata = 8, marcatore = {} }) {
  const { scrivibili, escluse } = pianificaGiornate(dal, al, periodo);
  const fine = fineGiornata(oreGiornata);
  const scritti = [], saltati = [...escluse], daGestireAMano = [];
  const perMese = new Map();
  for (const g of scrivibili) {
    const k = `${g.getFullYear()}_${g.getMonth() + 1}`;
    if (!perMese.has(k)) perMese.set(k, []);
    perMese.get(k).push(g);
  }
  for (const [k, giorniMese] of perMese) {
    const [anno, mese] = k.split("_").map(Number);
    const ref = doc(db, "presenze", `${orgId}_${uid}_${anno}_${mese}`);
    const snap = await getDoc(ref);
    const dati = snap.exists() ? snap.data() : null;
    if (dati?.stato && dati.stato !== "bozza") {
      giorniMese.forEach(g => daGestireAMano.push(fmtIt(g)));
      continue;
    }
    const aggiornamenti = {};
    for (const g of giorniMese) {
      const n = g.getDate();
      const esistente = dati?.giorni?.[n];
      if ((esistente?.timbrature || []).length) {
        const speciale = esistente.timbrature.find(x => x.speciale)?.commessaNome;
        saltati.push({ data: dataStr(g), motivo: speciale ? `già segnato come ${speciale}` : "risulta già lavorato" });
        continue;
      }
      const ts = new Date().toISOString();
      aggiornamenti[n] = { ...(esistente || {}), timbrature: [
        { tipo: "entrata", ora: "08:00", commessaId: "", commessaNome: voce, speciale: true, ...marcatore, timestamp: ts },
        { tipo: "uscita", ora: fine, commessaId: "", commessaNome: voce, speciale: true, oreForzate: Number(oreGiornata) || 8, ...marcatore, timestamp: ts },
      ] };
      scritti.push(dataStr(g));
    }
    if (!Object.keys(aggiornamenti).length) continue;
    if (dati) {
      const campi = { updatedAt: serverTimestamp() };
      Object.entries(aggiornamenti).forEach(([n, v]) => { campi[`giorni.${n}`] = v; });
      await updateDoc(ref, campi);
    } else {
      await setDoc(ref, { orgId, uid, anno, mese, stato: "bozza", giorni: aggiornamenti, updatedAt: serverTimestamp() });
    }
  }
  return { scritti, saltati, daGestireAMano };
}

/**
 * Permesso A ORE approvato (ottobre 2026): aggiunge «Permesso dalle … alle …» alla giornata del
 * cartellino, anche se quel giorno la persona lavora — il calcolo delle ore
 * (utils/segmentiGiornata.js) toglie il permesso dalle ore lavorate che lo comprendono.
 * Regole di sempre: un cartellino già inviato o approvato non si tocca (giorno «da gestire a
 * mano»); se in quell'orario c'è già Ferie, Permesso o Malattia non si scrive nulla e si
 * segnala; una seconda approvazione della stessa richiesta non duplica nulla.
 * @returns {{ scritti:string[], saltati:{data,motivo}[], daGestireAMano:string[] }}
 */
export async function scriviPermessoAOre({ orgId, uid, giorno, dalle, alle, marcatore = {} }) {
  const g = parseData(giorno);
  const a = minutiDaOra(dalle), b = minutiDaOra(alle);
  if (!g || a == null || b == null || b <= a) throw new Error("Orario del permesso non valido");
  const n = g.getDate(), anno = g.getFullYear(), mese = g.getMonth() + 1;
  const ref = doc(db, "presenze", `${orgId}_${uid}_${anno}_${mese}`);
  const snap = await getDoc(ref);
  const dati = snap.exists() ? snap.data() : null;
  if (dati?.stato && dati.stato !== "bozza") return { scritti: [], saltati: [], daGestireAMano: [fmtIt(g)] };
  const esistente = dati?.giorni?.[n];
  const timb = esistente?.timbrature || [];
  const chiave = Object.entries(marcatore)[0];
  if (chiave && timb.some(t => t[chiave[0]] === chiave[1])) return { scritti: [], saltati: [{ data: dataStr(g), motivo: "già scritto nel cartellino" }], daGestireAMano: [] };
  const occupato = segmentiGiornata(timb).segmenti.find(s => s.speciale && minutiDaOra(s.entrata) < b && minutiDaOra(s.uscita) > a);
  if (occupato) return { scritti: [], saltati: [{ data: dataStr(g), motivo: `in quell'orario c'è già ${occupato.commessaNome}` }], daGestireAMano: [] };
  const ts = new Date().toISOString();
  const nuove = [...timb,
    { tipo: "entrata", ora: dalle, commessaId: "", commessaNome: "Permesso", speciale: true, ...marcatore, timestamp: ts },
    { tipo: "uscita", ora: alle, commessaId: "", commessaNome: "Permesso", speciale: true, oreForzate: Math.round((b - a) / 60 * 100) / 100, ...marcatore, timestamp: ts },
  ];
  if (dati) await updateDoc(ref, { [`giorni.${n}`]: { ...(esistente || {}), timbrature: nuove }, updatedAt: serverTimestamp() });
  else await setDoc(ref, { orgId, uid, anno, mese, stato: "bozza", giorni: { [n]: { timbrature: nuove } }, updatedAt: serverTimestamp() });
  return { scritti: [dataStr(g)], saltati: [], daGestireAMano: [] };
}

/**
 * Registra l'assenza e la scrive nel cartellino.
 * @returns {{ id, scritti:string[], saltati:{data,motivo}[], daGestireAMano:string[] }}
 */
export async function registraAssenza({ orgId, uid, nomePersona, tipo, dal, al, protocollo = "", note = "", periodo, autore, oreGiornata = 8 }) {
  const errore = validaAssenza({ tipo, dal, al });
  if (errore) throw new Error(errore);
  const t = TIPI_ASSENZA.find(x => x.id === tipo);

  // 1) Il documento dell'assenza nasce per primo: il suo id marca le giornate scritte
  const rif = await addDoc(collection(db, "assenze"), {
    organizationId: orgId, uid, nomePersona: nomePersona || "",
    tipo, voce: t.voce, dal, al,
    protocollo: t.protocollo ? String(protocollo || "").trim() : "",
    note: String(note || "").trim().slice(0, 300),
    stato: "attiva",
    inseritaDa: autore.uid, inseritaDaNome: autore.nome || "", inseritaIl: serverTimestamp(),
    perContoDiAltri: autore.uid !== uid,
  });

  // 2) Giornate nel cartellino (funzione condivisa con l'approvazione di ferie e permessi)
  const { scritti, saltati, daGestireAMano } = await scriviGiornateSpeciali({
    orgId, uid, dal, al, voce: t.voce, periodo, oreGiornata, marcatore: { assenzaId: rif.id } });

  await updateDoc(rif, { giorniScritti: scritti, giorniSaltati: saltati, giorniDaGestireAMano: daGestireAMano });
  return { id: rif.id, scritti, saltati, daGestireAMano };
}

/** Annulla l'assenza: toglie dal cartellino SOLO le giornate che aveva scritto lei. */
export async function annullaAssenza({ assenza, orgId, autore }) {
  const perMese = new Map();
  for (const s of assenza.giorniScritti || []) {
    const d = parseData(s); if (!d) continue;
    const k = `${d.getFullYear()}_${d.getMonth() + 1}`;
    if (!perMese.has(k)) perMese.set(k, []);
    perMese.get(k).push(d.getDate());
  }
  const daGestireAMano = [];
  for (const [k, giorni] of perMese) {
    const [anno, mese] = k.split("_").map(Number);
    const ref = doc(db, "presenze", `${orgId}_${assenza.uid}_${anno}_${mese}`);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const dati = snap.data();
    if (dati.stato && dati.stato !== "bozza") {
      giorni.forEach(n => daGestireAMano.push(`${String(n).padStart(2, "0")}/${String(mese).padStart(2, "0")}/${anno}`));
      continue;
    }
    const campi = { updatedAt: serverTimestamp() };
    for (const n of giorni) {
      const g = dati.giorni?.[n];
      if (!g) continue;
      campi[`giorni.${n}`] = { ...g, timbrature: (g.timbrature || []).filter(x => x.assenzaId !== assenza.id) };
    }
    await updateDoc(ref, campi);
  }
  await updateDoc(doc(db, "assenze", assenza.id), {
    stato: "annullata", annullataDa: autore.uid, annullataDaNome: autore.nome || "", annullataIl: serverTimestamp(),
    ...(daGestireAMano.length ? { annullamentoDaGestireAMano: daGestireAMano } : {}),
  });
  return { daGestireAMano };
}

/** Aggiunge o corregge il numero di protocollo (spesso arriva qualche giorno dopo). */
export async function aggiornaProtocollo(assenzaId, protocollo) {
  await updateDoc(doc(db, "assenze", assenzaId), { protocollo: String(protocollo || "").trim(), protocolloAggiornatoIl: serverTimestamp() });
}
